'use client';

import Link from 'next/link';
import { LinkIcon } from '@heroicons/react/24/solid';
import { ProposalCard } from '@/app/types/proposals';
import { UniversalAvatar } from '@/app/components/UniversalAvatar';
import { formatDate } from '@/app/helpers/formatDate';
import { useAtom } from 'jotai';
import { userInfoAtom } from '@/app/atoms/users';
import { allProposalsAtom } from '@/app/atoms/proposals';
import { useEffect, useState } from 'react';
import { UsersAccountReturnType } from '@/app/types/account';
import { formatVotesValue } from '@/app/helpers/formatVotes';

interface ProposalCardsProps {
  proposal: ProposalCard;
  index?: number;
}

const ProposalCards: React.FC<ProposalCardsProps> = ({ proposal, index }) => {
  const [users] = useAtom(userInfoAtom);
  const [allProposals] = useAtom(allProposalsAtom);
  const [settings, setSettings] = useState<UsersAccountReturnType | undefined>();

  useEffect(() => {
    const newSettings = users.find(
      (user: UsersAccountReturnType) => user.wallet_address === proposal.proposer,
    );
    setSettings(newSettings);
  }, [proposal.proposer, users]);

  // strip "KIP 13,14, 15 & 17" prefixes
  const cleanTitle = proposal.title
    .replace(/^KIP - 13:\s*/i, '')
    .replace(/^KIP-14:\s*/i, '')
    .replace(/^KIP-15:\s*/i, '')
    .replace(/^KIP 17:\s*/i, '')
    .trim();

  const formattedTitle = `KIP ${index}: ${cleanTitle}`;

  return (
    <div className="flex flex-col p-4 max-w-full">
      <div className="flex flex-col items-start sm:items-center sm:justify-between sm:flex-row">
        <div className="sm:w-3/5 font-bold">
          {proposal.proposal_id ===
          '37695651889739924036315130198910549192250601445193408333915933331571403831552' ? (
            <span className="flex text-link">{formattedTitle}</span>
          ) : (
            <Link
              href={{
                pathname: `/karratco/view/proposal/${proposal.proposal_id}`,
                query: { kip: `KIP ${index}` },
              }}
              className="flex text-link hover:text-hover"
            >
              {formattedTitle}
              <LinkIcon className="w-4 h-4 ml-2" />
            </Link>
          )}
        </div>
        <div className="flex w-2/5 justify-between items-center max-sm:hidden">
          <div className="flex justify-center items-center whitespace-nowrap w-1/3 text-accent">
            {formatVotesValue(proposal.votes_for)}
          </div>
          <div className="flex justify-center items-center whitespace-nowrap w-1/3 text-defeated">
            {formatVotesValue(proposal.votes_against)}
          </div>
          <p className="flex justify-end items-center whitespace-nowrap w-1/3">
            {proposal.voters ?? 0}
          </p>
        </div>
      </div>
      <div className="flex flex-col max-w-max justify-center">
        <div className="flex items-center text-xs mt-2 xs:mt-0">
          <span className="mr-1 flex items-center uppercase text-xxs">
            {proposal.created_at ? formatDate(new Date(proposal.created_at)) : <p>-</p>}
          </span>
        </div>
        <div className="flex items-center gap-1 text-xs my-2">
          <UniversalAvatar address={proposal.proposer} width={15} height={15} size={15} />
        </div>
        <div
          className={`px-2 mt-2 text-xs rounded-full max-w-max ${proposal.state.bgColor} ${proposal.state.textColor}`}
        >
          {proposal.state.text}
        </div>
      </div>
    </div>
  );
};

export default ProposalCards;
